import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { kakaoLogin } from '../lib/kakao'
import { naverLogin } from '../lib/naver'
import { Roles, RoleLabels } from '../auth/rbac'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import InlineError from '../ui/InlineError'
import Loading from '../ui/Loading'
import { PrivacyContent, TermsContent } from './legal/LegalContents'
import LegalPolicyPublicSection from './legal/LegalPolicyPublicSection'
import { GoogleLogin } from '@react-oauth/google'

export default function SignupPage() {
  const navigate = useNavigate()
  const [orgOptions, setOrgOptions] = useState([])
  const [roleOptions, setRoleOptions] = useState([])
  const [legal, setLegal] = useState({
    termsVersion: '2026-04-14',
    privacyVersion: '2026-04-14',
    marketingVersion: '2026-04-14',
    refundPolicyVersion: '2026-04-17',
    termsPath: '/terms',
    privacyPath: '/privacy',
    refundPolicyPath: '/refund-policy',
  })
  const [form, setForm] = useState({
    name: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    referrerCode: '',
    orgId: '',
    requestedRole: ''
  })
  /** 이메일 = local @ (선택 도메인 | 직접입력 도메인) */
  const [emailLocal, setEmailLocal] = useState('')
  const [emailDomainSel, setEmailDomainSel] = useState('naver.com')
  const [emailDomainCustom, setEmailDomainCustom] = useState('')
  const [prefilledReferralCode, setPrefilledReferralCode] = useState(false)
  const [personalJoin, setPersonalJoin] = useState(false)
  const [affiliationText, setAffiliationText] = useState('')
  const [personalJoinConfirmOpen, setPersonalJoinConfirmOpen] = useState(false)

  // 개인 가입 선택 시 GA_MANAGER 역할로 자동 설정
  useEffect(() => {
    if (personalJoin) {
      setForm(prev => ({
        ...prev,
        requestedRole: Roles.GA_MANAGER
      }))
    }
  }, [personalJoin])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const referralCode = String(params.get('ref') || params.get('referrerCode') || '').trim()
    if (!referralCode) return
    setForm(prev => ({ ...prev, referrerCode: referralCode }))
    setPrefilledReferralCode(true)
  }, [])
  const [agreements, setAgreements] = useState({
    terms: false,
    privacy: false,
    marketing: false,
  })
  const [legalModal, setLegalModal] = useState({ open: false, doc: null }) // doc: 'terms' | 'privacy'
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [socialLinkModal, setSocialLinkModal] = useState({
    open: false,
    signupId: null,
    signupLinkToken: null,
    linking: false,
    error: null,
    linked: { GOOGLE: false, KAKAO: false, NAVER: false },
  })

  const googleEnabled = String(process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim().length > 0
  const kakaoEnabled = String(process.env.REACT_APP_KAKAO_JS_KEY || '').trim().length > 0
  const naverEnabled = String(process.env.REACT_APP_NAVER_CLIENT_ID || '').trim().length > 0
  const socialConfigured = googleEnabled || kakaoEnabled || naverEnabled
  const [orgCategory, setOrgCategory] = useState('')
  const [orgL1, setOrgL1] = useState('')
  const [orgL2, setOrgL2] = useState('')
  const [orgL3, setOrgL3] = useState('')

  const [emailCheck, setEmailCheck] = useState({
    status: 'idle', // idle | invalid | checking | available | unavailable | error
    checkedEmail: '',
    message: null,
  })

  const [phoneCheck, setPhoneCheck] = useState({
    status: 'idle', // idle | invalid | checking | available | unavailable | error
    checkedPhone: '',
    message: null,
  })

  const isValidEmail = (v) => {
    if (!v) return false
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim())
  }

  const EMAIL_DOMAIN_PRESETS = useMemo(
    () => [
      { value: 'naver.com', label: 'naver.com' },
      { value: 'gmail.com', label: 'gmail.com' },
      { value: 'daum.net', label: 'daum.net' },
      { value: 'hanmail.net', label: 'hanmail.net' },
      { value: 'kakao.com', label: 'kakao.com' },
      { value: 'nate.com', label: 'nate.com' },
      { value: 'outlook.com', label: 'outlook.com' },
    ],
    []
  )

  const fullEmail = useMemo(() => {
    const loc = String(emailLocal || '').trim()
    if (!loc) return ''
    if (emailDomainSel === '__custom__') {
      const dom = String(emailDomainCustom || '')
        .trim()
        .replace(/^@+/, '')
      return dom ? `${loc}@${dom}` : ''
    }
    return `${loc}@${emailDomainSel}`
  }, [emailLocal, emailDomainSel, emailDomainCustom])

  const normalizePhone = (v) => String(v || '').replace(/[^0-9]/g, '')

  // 표시용 포맷: 01012345678 -> 010-1234-5678 (타이핑/붙여넣기 대응)
  const formatPhoneDisplay = (v) => {
    const d = normalizePhone(v)
    if (!d) return ''
    if (d.length <= 3) return d
    if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`
    // 3-4-나머지(최대 010-1234-5678 형태)
    return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`
  }

  const isValidPhone = (v) => {
    const d = normalizePhone(v)
    return d.length >= 9 && d.length <= 12
  }

  const checkEmailNow = async (rawEmail) => {
    const email = String(rawEmail || '').trim()
    if (!email) {
      const next = { status: 'invalid', checkedEmail: '', message: '이메일을 입력해 주세요.' }
      setEmailCheck(next)
      return { available: false, message: next.message }
    }
    if (!isValidEmail(email)) {
      const next = { status: 'invalid', checkedEmail: email, message: '이메일 형식을 확인해 주세요.' }
      setEmailCheck(next)
      return { available: false, message: next.message }
    }

    // 이미 확인된 값이면 그대로 반환
    if (emailCheck.checkedEmail === email && emailCheck.status === 'available') {
      return { available: true }
    }
    if (emailCheck.checkedEmail === email && emailCheck.status === 'unavailable') {
      return { available: false, message: emailCheck.message || '이미 사용 중인 이메일입니다.' }
    }

    setEmailCheck(prev => ({ ...prev, status: 'checking', checkedEmail: email, message: null }))
    try {
      const res = await api.get('/signups/public/check-email', { params: { email } })
      const payload = res?.data?.data ?? res?.data
      const available = Boolean(payload?.available)
      const reason = payload?.reason
      const next = available
        ? { status: 'available', checkedEmail: email, message: '사용 가능한 이메일입니다.' }
        : { status: 'unavailable', checkedEmail: email, message: reason || '이미 사용 중인 이메일입니다.' }
      setEmailCheck(next)
      return { available, message: next.message }
    } catch (err) {
      const next = { status: 'error', checkedEmail: email, message: '이메일 중복 확인에 실패했습니다.' }
      setEmailCheck(next)
      return { available: false, message: next.message }
    }
  }

  const debouncedEmail = useDebouncedValue(fullEmail, 400)

  const checkPhoneNow = async (rawPhone) => {
    const phoneNumber = String(rawPhone || '').trim()
    if (!phoneNumber) {
      const next = { status: 'invalid', checkedPhone: '', message: '휴대폰 번호를 입력해 주세요.' }
      setPhoneCheck(next)
      return { available: false, message: next.message }
    }
    if (!isValidPhone(phoneNumber)) {
      const next = { status: 'invalid', checkedPhone: phoneNumber, message: '휴대폰 번호 형식을 확인해 주세요.' }
      setPhoneCheck(next)
      return { available: false, message: next.message }
    }

    // 이미 확인된 값이면 그대로 반환
    if (phoneCheck.checkedPhone === phoneNumber && phoneCheck.status === 'available') {
      return { available: true }
    }
    if (phoneCheck.checkedPhone === phoneNumber && phoneCheck.status === 'unavailable') {
      return { available: false, message: phoneCheck.message || '이미 사용 중인 휴대폰 번호입니다.' }
    }

    setPhoneCheck(prev => ({ ...prev, status: 'checking', checkedPhone: phoneNumber, message: null }))
    try {
      const res = await api.get('/signups/public/check-phone', { params: { phoneNumber } })
      const payload = res?.data?.data ?? res?.data
      const available = Boolean(payload?.available)
      const reason = payload?.reason
      const next = available
        ? { status: 'available', checkedPhone: phoneNumber, message: '사용 가능한 휴대폰 번호입니다.' }
        : { status: 'unavailable', checkedPhone: phoneNumber, message: reason || '이미 사용 중인 휴대폰 번호입니다.' }
      setPhoneCheck(next)
      return { available, message: next.message }
    } catch {
      const next = { status: 'error', checkedPhone: phoneNumber, message: '휴대폰 번호 중복 확인에 실패했습니다.' }
      setPhoneCheck(next)
      return { available: false, message: next.message }
    }
  }

  const debouncedPhone = useDebouncedValue(form.phoneNumber, 400)

  useEffect(() => {
    setEmailCheck((prev) => ({ ...prev, status: 'idle', checkedEmail: '', message: null }))
  }, [emailLocal, emailDomainSel, emailDomainCustom])

  // 입력 중에도 자동으로(디바운스) 중복 확인하여 사용자 경험 개선
  useEffect(() => {
    const email = String(debouncedEmail || '').trim()
    if (!email) {
      setEmailCheck({ status: 'idle', checkedEmail: '', message: null })
      return
    }
    if (!isValidEmail(email)) {
      setEmailCheck(prev => ({ ...prev, status: 'invalid', checkedEmail: email, message: '이메일 형식을 확인해 주세요.' }))
      return
    }
    if (emailCheck.checkedEmail === email && (emailCheck.status === 'available' || emailCheck.status === 'unavailable')) {
      return
    }

    let ignore = false
    ;(async () => {
      setEmailCheck(prev => ({ ...prev, status: 'checking', checkedEmail: email, message: null }))
      try {
        const res = await api.get('/signups/public/check-email', { params: { email } })
        const payload = res?.data?.data ?? res?.data
        const available = Boolean(payload?.available)
        const reason = payload?.reason
        const next = available
          ? { status: 'available', checkedEmail: email, message: '사용 가능한 이메일입니다.' }
          : { status: 'unavailable', checkedEmail: email, message: reason || '이미 사용 중인 이메일입니다.' }
        if (!ignore) setEmailCheck(next)
      } catch {
        if (!ignore) setEmailCheck({ status: 'error', checkedEmail: email, message: '이메일 중복 확인에 실패했습니다.' })
      }
    })()

    return () => { ignore = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEmail])

  useEffect(() => {
    const phoneNumber = String(debouncedPhone || '').trim()
    if (!phoneNumber) {
      setPhoneCheck({ status: 'idle', checkedPhone: '', message: null })
      return
    }
    if (!isValidPhone(phoneNumber)) {
      setPhoneCheck(prev => ({ ...prev, status: 'invalid', checkedPhone: phoneNumber, message: '휴대폰 번호 형식을 확인해 주세요.' }))
      return
    }
    if (phoneCheck.checkedPhone === phoneNumber && (phoneCheck.status === 'available' || phoneCheck.status === 'unavailable')) {
      return
    }

    let ignore = false
    ;(async () => {
      setPhoneCheck(prev => ({ ...prev, status: 'checking', checkedPhone: phoneNumber, message: null }))
      try {
        const res = await api.get('/signups/public/check-phone', { params: { phoneNumber } })
        const payload = res?.data?.data ?? res?.data
        const available = Boolean(payload?.available)
        const reason = payload?.reason
        const next = available
          ? { status: 'available', checkedPhone: phoneNumber, message: '사용 가능한 휴대폰 번호입니다.' }
          : { status: 'unavailable', checkedPhone: phoneNumber, message: reason || '이미 사용 중인 휴대폰 번호입니다.' }
        if (!ignore) setPhoneCheck(next)
      } catch {
        if (!ignore) setPhoneCheck({ status: 'error', checkedPhone: phoneNumber, message: '휴대폰 번호 중복 확인에 실패했습니다.' })
      }
    })()

    return () => { ignore = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPhone])

  // 최상위(생보/손보/GA)별 역할 그룹
  const gaRoleGroup = useMemo(
    () => [
      // 정책: GA는 대표/관리자/매니저/설계사 4단계만 사용한다.
      // 노출/정렬: 설계사 → 매니저 → 관리자 → 대표
      Roles.GA_PLANNER,
      Roles.GA_OPERATION_MANAGER,
      Roles.GA_MANAGER,
      Roles.GA_LEADER,
    ],
    []
  )

  const insurerRoleGroup = useMemo(
    () => [
      // 정책: 원수사는 대표/관리자/매니저/설계사 4단계만 사용한다.
      // 노출/정렬: 설계사 → 매니저 → 관리자 → 대표
      Roles.INSURER_PLANNER,
      Roles.INSURER_OPERATION_MANAGER,
      Roles.INSURER_MANAGER,
      Roles.INSURER_LEADER,         // 대표
    ],
    []
  )

  const orderByRoleGroup = React.useCallback((codes, preferredOrder) => {
    const set = new Set((codes || []).map(String))
    const out = []
    for (const c of (preferredOrder || [])) {
      if (set.has(String(c))) out.push(String(c))
    }
    // 혹시 모를 잔여(정의되지 않은 역할/추가 역할)는 뒤에 붙인다.
    for (const c of (codes || [])) {
      const s = String(c)
      if (!out.includes(s)) out.push(s)
    }
    return out
  }, [])

  useEffect(() => {
    async function load() {
      try {
        const [orgRes, legalRes] = await Promise.all([
          api.get('/orgs/public-options'),
          api.get('/signups/public/legal').catch(() => null),
        ])
        const orgData = orgRes?.data?.data ?? orgRes?.data ?? []
        setOrgOptions(orgData)

        const legalPayload = legalRes?.data?.data ?? legalRes?.data ?? null
        if (legalPayload) {
          setLegal(prev => ({
            ...prev,
            termsVersion: legalPayload?.termsVersion || prev.termsVersion,
            privacyVersion: legalPayload?.privacyVersion || prev.privacyVersion,
            marketingVersion: legalPayload?.marketingVersion || prev.marketingVersion,
            refundPolicyVersion: legalPayload?.refundPolicyVersion || prev.refundPolicyVersion,
            termsPath: legalPayload?.termsPath || prev.termsPath,
            privacyPath: legalPayload?.privacyPath || prev.privacyPath,
            refundPolicyPath: legalPayload?.refundPolicyPath || prev.refundPolicyPath,
          }))
        }

        // 직접 회원가입에서 선택 가능한 역할(정책): "실사용 역할"만 노출한다.
        // - GA: 대표/관리자/설계사
        // - 원수사: 대표/관리자/매니저/설계사
        const allowedCodes = Array.from(new Set([
          ...gaRoleGroup,
          ...insurerRoleGroup,
        ].map(String)))
        setRoleOptions(allowedCodes)
      } catch (e) {
        setError('조직/역할 정보를 불러오지 못했습니다.')
      }
    }
    load()
  }, [gaRoleGroup, insurerRoleGroup])

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === 'phoneNumber') {
      // 휴대폰 변경 시 기존 중복확인 결과는 무효
      setPhoneCheck(prev => ({ ...prev, status: 'idle', checkedPhone: '', message: null }))
      // XXX-XXXX-XXXX 형태로 정규화해서 표시
      const formatted = formatPhoneDisplay(value)
      setForm(prev => ({ ...prev, [name]: formatted }))
      return
    }
    if (name === 'referrerCode') {
      setPrefilledReferralCode(false)
    }
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    // 이메일 중복 확인(최종 방어선)
    const emailResult = await checkEmailNow(fullEmail)
    if (!emailResult.available) {
      setError(emailResult.message || '이메일 중복 확인에 실패했습니다.')
      return
    }

    // 휴대폰 번호 중복 확인(최종 방어선)
    const phoneResult = await checkPhoneNow(form.phoneNumber)
    if (!phoneResult.available) {
      setError(phoneResult.message || '휴대폰 번호 중복 확인에 실패했습니다.')
      return
    }

    if (form.password !== form.confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.')
      return
    }
    if (form.password.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다.')
      return
    }
    if (personalJoin) {
      if (personalRootOrgId == null) {
        setError('개인 가입용 조직 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.')
        return
      }
      if (!form.requestedRole) {
        setError('역할을 선택해 주세요.')
        return
      }
    } else {
      if (!form.orgId || !form.requestedRole) {
        setError('조직과 역할을 선택해 주세요.')
        return
      }
      // 조직은 최소 2차(루트 하위)까지 선택해야 한다.
      const selectedOrg = orgOptions.find(o => String(o?.id) === String(form.orgId))
      if (!selectedOrg || !selectedOrg.parentOrgId) {
        setError('조직은 최소 2차 조직(루트 하위 조직)까지 선택해 주세요.')
        return
      }
    }
    if (!agreements.terms || !agreements.privacy) {
      setError('이용약관 및 개인정보 처리방침 동의가 필요합니다.')
      return
    }

    setLoading(true)
    try {
      const res = await api.post('/signups/public', {
        email: fullEmail,
        name: form.name,
        password: form.password,
        phoneNumber: String(form.phoneNumber || '').trim(),
        referrerCode: String(form.referrerCode || '').trim() || undefined,
        orgId: personalJoin ? Number(personalRootOrgId) : Number(form.orgId),
        requestedRole: form.requestedRole,
        personalJoin: personalJoin || undefined,
        affiliationText: personalJoin ? (affiliationText || '').trim() || undefined : undefined,
        affiliationOrgCreateConsent: undefined, // 더 이상 사용하지 않음 (하위 호환성을 위해 null로 전송)
        termsAccepted: Boolean(agreements.terms),
        termsVersion: legal.termsVersion || 'unknown',
        privacyAccepted: Boolean(agreements.privacy),
        privacyVersion: legal.privacyVersion || 'unknown',
        marketingAccepted: Boolean(agreements.marketing),
        marketingVersion: legal.marketingVersion || null,
        consentChannel: 'WEB',
      })
      const payload = res?.data?.data ?? res?.data ?? {}
      const signupId = payload?.id
      const signupLinkToken = payload?.signupLinkToken
      setSuccess('가입 신청이 접수되었습니다. 승인 후 로그인할 수 있습니다.')
      try {
        window?.toast?.show?.('가입 신청이 접수되었습니다. 승인 후 로그인할 수 있습니다.', 'success')
      } catch { /* no toast provider */ }
      // 가입 직후: 소셜 연동(예약) 팝업 노출 (키가 설정된 경우에만)
      if (socialConfigured) {
        setSocialLinkModal({
          open: true,
          signupId: signupId ?? null,
          signupLinkToken: signupLinkToken ?? null,
          linking: false,
          error: null,
          linked: { GOOGLE: false, KAKAO: false, NAVER: false },
        })
      } else {
        // 키 미설정 상태에서는 일반 사용자에게 소셜 UI를 숨긴다.
        navigate('/login', { state: { emailHint: String(fullEmail || '').trim() } })
      }
    } catch (err) {
      const msg = err?.response?.data?.error?.message
        || err?.response?.data?.message
        || err?.message
        || '가입 신청에 실패했습니다.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const closeSocialModalAndGoLogin = () => {
    setSocialLinkModal(prev => ({ ...prev, open: false }))
    navigate('/login')
  }

  const reserveSocialLink = async (provider, { idToken, accessToken } = {}) => {
    const signupId = socialLinkModal.signupId
    const signupLinkToken = socialLinkModal.signupLinkToken
    if (!signupId || !signupLinkToken) {
      setSocialLinkModal(prev => ({ ...prev, error: '가입 신청 정보가 없어 소셜 연동을 진행할 수 없습니다.' }))
      return
    }
    setSocialLinkModal(prev => ({ ...prev, linking: true, error: null }))
    try {
      await api.post(`/signups/public/social-links/${String(provider).toLowerCase()}`, {
        signupId,
        signupLinkToken,
        idToken,
        accessToken,
      })
      setSocialLinkModal(prev => ({
        ...prev,
        linking: false,
        linked: { ...prev.linked, [String(provider).toUpperCase()]: true },
        error: null,
      }))
    } catch (err) {
      const msg =
        err?.response?.data?.data?.message
        || err?.response?.data?.message
        || err?.response?.data?.error?.message
        || err?.message
        || '소셜 연동에 실패했습니다.'
      setSocialLinkModal(prev => ({ ...prev, linking: false, error: String(msg) }))
    }
  }

  const roleLabel = (code) => {
    return RoleLabels[code] || code
  }

  // 상단 조직 구분에 따라 신청 가능한 역할 필터링
  const filteredRoleOptions = useMemo(() => {
    if (!orgCategory) {
      // 구분이 없을 때는 GA → INSURER 순으로 보이도록 기본 정렬
      return orderByRoleGroup(roleOptions, [...gaRoleGroup, ...insurerRoleGroup])
    }
    if (orgCategory === 'GA') {
      const candidates = roleOptions.filter(code => gaRoleGroup.includes(code))
      return orderByRoleGroup(candidates, gaRoleGroup)
    }
    if (orgCategory === 'PERSONAL') {
      // 개인 가입: GA/원수사 역할 모두 선택 가능
      return orderByRoleGroup(roleOptions, [...gaRoleGroup, ...insurerRoleGroup])
    }
    if (orgCategory === 'INS_LIFE' || orgCategory === 'INS_NONLIFE') {
      const candidates = roleOptions.filter(code => insurerRoleGroup.includes(code))
      return orderByRoleGroup(candidates, insurerRoleGroup)
    }
    return orderByRoleGroup(roleOptions, [...gaRoleGroup, ...insurerRoleGroup])
  }, [orgCategory, roleOptions, gaRoleGroup, insurerRoleGroup, orderByRoleGroup])

  const childrenOf = React.useCallback(
    (parentId) => orgOptions.filter(o => o.parentOrgId === parentId),
    [orgOptions]
  )

  const lifeRootIds = useMemo(
    () =>
      orgOptions
        .filter(o => !o.parentOrgId && o.type === 'INSURER' && (o.name.includes('생명') || o.name.includes('생보')))
        .map(o => o.id),
    [orgOptions]
  )

  const nonLifeRootIds = useMemo(
    () =>
      orgOptions
        .filter(o => !o.parentOrgId && o.type === 'INSURER' && (o.name.includes('손해') || o.name.includes('화재') || o.name.includes('해상')))
        .map(o => o.id),
    [orgOptions]
  )

  const gaRootIds = useMemo(
    () => orgOptions.filter(o => !o.parentOrgId && o.type === 'GA').map(o => o.id),
    [orgOptions]
  )

  const level1Options = useMemo(() => {
    if (!orgCategory) return []
    if (orgCategory === 'GA') {
      return orgOptions.filter(o => gaRootIds.includes(o.id))
    }
    if (orgCategory === 'INS_LIFE') {
      return orgOptions.filter(o => lifeRootIds.includes(o.parentOrgId))
    }
    if (orgCategory === 'INS_NONLIFE') {
      return orgOptions.filter(o => nonLifeRootIds.includes(o.parentOrgId))
    }
    return []
  }, [orgCategory, orgOptions, gaRootIds, lifeRootIds, nonLifeRootIds])

  const level2Options = useMemo(() => {
    if (!orgL1) return []
    return childrenOf(Number(orgL1))
  }, [orgL1, childrenOf])

  const level3Options = useMemo(() => {
    if (!orgL2) return []
    return childrenOf(Number(orgL2))
  }, [orgL2, childrenOf])

  // 개인 루트 조직 ID (개인 가입 시 사용, 생보/손보/GA와 동일한 루트 레벨)
  const personalRootOrgId = useMemo(() => {
    const parentId = (o) => o?.parentOrgId ?? o?.parent_org_id
    return orgOptions.find(o => {
      if (o?.name !== '개인') return false
      const type = String(o?.type ?? '').toUpperCase()
      if (type !== 'PERSONAL') return false
      const pid = parentId(o)
      return pid == null || pid === '' || pid === undefined
    })?.id
  }, [orgOptions])

  // 최종 선택된 orgId 동기화 (가장 하위 선택값 우선). 개인 가입 시 루트 "개인" 고정
  useEffect(() => {
    if (personalJoin && personalRootOrgId != null) {
      setForm(prev => ({ ...prev, orgId: Number(personalRootOrgId) }))
      setOrgCategory('PERSONAL')
      return
    }
    const selected =
      orgL3 || orgL2 || orgL1 || ''
    setForm(prev => ({ ...prev, orgId: selected ? Number(selected) : '' }))
  }, [personalJoin, personalRootOrgId, orgL1, orgL2, orgL3])

  const handleCategoryChange = (e) => {
    const v = e.target.value
    setOrgCategory(v)
    setOrgL1('')
    setOrgL2('')
    setOrgL3('')

    // 선택 가능한 역할도 함께 갱신
    let nextRole = ''
    if (v === 'GA') {
      const candidates = roleOptions.filter(code => gaRoleGroup.includes(code))
      const ordered = orderByRoleGroup(candidates, gaRoleGroup)
      nextRole = ordered[0] || ''
    } else if (v === 'INS_LIFE' || v === 'INS_NONLIFE') {
      const candidates = roleOptions.filter(code => insurerRoleGroup.includes(code))
      const ordered = orderByRoleGroup(candidates, insurerRoleGroup)
      nextRole = ordered[0] || ''
    } else {
      // 구분을 비운 경우 역할도 초기화
      nextRole = ''
    }

    setForm(prev => ({
      ...prev,
      orgId: '',
      requestedRole: nextRole,
    }))
  }

  const handleLevel1Change = (e) => {
    const v = e.target.value
    setOrgL1(v)
    setOrgL2('')
    setOrgL3('')
  }

  const handleLevel2Change = (e) => {
    const v = e.target.value
    setOrgL2(v)
    setOrgL3('')
  }

  const handleLevel3Change = (e) => {
    const v = e.target.value
    setOrgL3(v)
  }

  const requiredAgreed = agreements.terms && agreements.privacy
  const closeLegalModal = () => setLegalModal({ open: false, doc: null })
  const enablePersonalJoin = () => setPersonalJoin(true)
  const disablePersonalJoin = () => {
    setPersonalJoin(false)
    setAffiliationText('')
    setForm(prev => ({
      ...prev,
      requestedRole: ''
    }))
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      {personalJoinConfirmOpen && (
        <div className="fixed inset-0 z-[1050]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPersonalJoinConfirmOpen(false)} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-lg rounded-lg border bg-white shadow-xl">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <div className="font-semibold text-slate-900">개인회원으로 가입하시겠습니까?</div>
                <button
                  type="button"
                  className="text-sm text-slate-500 hover:text-slate-800"
                  onClick={() => setPersonalJoinConfirmOpen(false)}
                >
                  닫기
                </button>
              </div>
              <div className="space-y-4 px-4 py-4 text-sm text-slate-700">
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                  <div className="font-medium text-slate-900">회원유형 안내</div>
                  <div className="mt-2 space-y-2 text-sm leading-6">
                    <p><span className="font-medium text-slate-900">개인회원</span>은 조직을 선택하지 않고 개인 단위로 가입하는 회원 유형입니다.</p>
                    <p><span className="font-medium text-slate-900">조직회원</span>은 회사, 지점 등 특정 조직 소속으로 가입하여 해당 조직 기준으로 서비스를 이용하는 회원 유형입니다.</p>
                  </div>
                </div>
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
                  회사 또는 특정 조직 소속으로 이용하실 경우에는 반드시 조직회원으로 가입해 주세요.
                  개인회원으로 가입 후 승인된 계정은 조직회원으로 바로 변경되지 않을 수 있으며, 변경이 필요한 경우 고객 문의가 필요할 수 있습니다.
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
                <button
                  type="button"
                  className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
                  onClick={() => setPersonalJoinConfirmOpen(false)}
                >
                  다시 선택하기
                </button>
                <button
                  type="button"
                  className="rounded bg-brand-navy px-3 py-2 text-sm text-white hover:bg-brand-navy-dark"
                  onClick={() => {
                    enablePersonalJoin()
                    setPersonalJoinConfirmOpen(false)
                  }}
                >
                  개인회원으로 계속
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {socialLinkModal.open && (
        <div className="fixed inset-0 z-[1100]">
          <div className="absolute inset-0 bg-black/40" onClick={() => closeSocialModalAndGoLogin()} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-md rounded-lg bg-white shadow-xl border p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-lg font-semibold text-slate-900">소셜 로그인 연동</div>
                  <div className="mt-1 text-sm text-slate-600">
                    지금 연동해두면 <span className="font-semibold">승인 후 바로</span> 구글/카카오로 로그인할 수 있어요.
                  </div>
                </div>
                <button type="button" className="text-sm text-slate-500 hover:text-slate-800" onClick={closeSocialModalAndGoLogin}>
                  닫기
                </button>
              </div>

              {socialLinkModal.error && (
                <div className="mt-3 text-sm text-red-700 whitespace-pre-line">
                  {socialLinkModal.error}
                </div>
              )}

              <div className="mt-4 space-y-3">
                <div className="text-xs text-slate-500">
                  연동은 로그인(사용)과 다릅니다. 승인 전에는 로그인할 수 없습니다.
                </div>

                {(googleEnabled) && (
                  <div className="rounded border p-3">
                    <div className="text-sm font-medium text-slate-800 flex items-center justify-between">
                      <span>Google</span>
                      {socialLinkModal.linked.GOOGLE && <span className="text-xs text-emerald-700">연동됨</span>}
                    </div>
                    <div className="mt-2">
                      <GoogleLogin
                        useOneTap={false}
                        onSuccess={(cred) => {
                          const idToken = cred?.credential
                          if (!idToken) {
                            setSocialLinkModal(prev => ({ ...prev, error: '구글 토큰을 받지 못했습니다.' }))
                            return
                          }
                          reserveSocialLink('google', { idToken })
                        }}
                        onError={() => setSocialLinkModal(prev => ({ ...prev, error: '구글 로그인(연동)에 실패했습니다.' }))}
                        text={socialLinkModal.linked.GOOGLE ? 'continue_with' : 'continue_with'}
                        width="100%"
                      />
                    </div>
                  </div>
                )}

                {(kakaoEnabled) && (
                  <div className="rounded border p-3">
                    <div className="text-sm font-medium text-slate-800 flex items-center justify-between">
                      <span>Kakao</span>
                      {socialLinkModal.linked.KAKAO && <span className="text-xs text-emerald-700">연동됨</span>}
                    </div>
                    <div className="mt-2">
                      <button
                        type="button"
                        className="w-full rounded bg-[#FEE500] text-[#191919] py-2 text-sm font-semibold disabled:opacity-50"
                        disabled={socialLinkModal.linking}
                        onClick={async () => {
                          try {
                            const accessToken = await kakaoLogin({ scope: 'account_email' })
                            await reserveSocialLink('kakao', { accessToken })
                          } catch (e) {
                            setSocialLinkModal(prev => ({ ...prev, error: '카카오 로그인(연동)에 실패했습니다.' }))
                          }
                        }}
                      >
                        카카오로 연동하기
                      </button>
                    </div>
                  </div>
                )}

                {(naverEnabled) && (
                  <div className="rounded border p-3">
                    <div className="text-sm font-medium text-slate-800 flex items-center justify-between">
                      <span>Naver</span>
                      {socialLinkModal.linked.NAVER && <span className="text-xs text-emerald-700">연동됨</span>}
                    </div>
                    <div className="mt-2">
                      <button
                        type="button"
                        className="w-full rounded bg-[#03C75A] text-white py-2 text-sm font-semibold disabled:opacity-50"
                        disabled={socialLinkModal.linking}
                        onClick={async () => {
                          try {
                            const { code, state, redirectUri } = await naverLogin({})
                            await reserveSocialLink('naver', { code, state, redirectUri })
                          } catch (e) {
                            setSocialLinkModal(prev => ({ ...prev, error: '네이버 로그인(연동)에 실패했습니다.' }))
                          }
                        }}
                      >
                        네이버로 연동하기
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
                    onClick={closeSocialModalAndGoLogin}
                    disabled={socialLinkModal.linking}
                  >
                    나중에 할게요
                  </button>
                  <button
                    type="button"
                    className="rounded bg-brand-navy text-white px-3 py-2 text-sm disabled:opacity-50"
                    onClick={closeSocialModalAndGoLogin}
                    disabled={socialLinkModal.linking}
                  >
                    로그인 화면으로
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {legalModal.open && (
        <div className="fixed inset-0 z-[1000]">
          <div className="absolute inset-0 bg-black/40" onClick={closeLegalModal} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl border">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div className="font-semibold text-slate-900">
                  {legalModal.doc === 'terms' ? '이용약관' : '개인정보 처리방침'}
                  <span className="ml-2 text-xs text-slate-500">
                    (버전: {legalModal.doc === 'terms' ? (legal.termsVersion || '-') : (legal.privacyVersion || '-')})
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <a
                    className="text-xs text-slate-600 hover:text-brand-sky"
                    href={legalModal.doc === 'terms' ? (legal.termsPath || '/terms') : (legal.privacyPath || '/privacy')}
                    target="_blank"
                    rel="noreferrer"
                  >
                    전체 화면
                  </a>
                  <button type="button" className="text-sm text-slate-600 hover:text-slate-900" onClick={closeLegalModal}>
                    닫기
                  </button>
                </div>
              </div>
              <div className="max-h-[70vh] overflow-auto px-4 py-4">
                <LegalPolicyPublicSection
                  docCode={legalModal.doc === 'terms' ? 'TERMS' : 'PRIVACY'}
                  fallback={legalModal.doc === 'terms' ? <TermsContent /> : <PrivacyContent />}
                />
              </div>
              <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
                {legalModal.doc === 'terms' && (
                  <button
                    type="button"
                    className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
                    onClick={() => {
                      setAgreements(prev => ({ ...prev, terms: true }))
                      closeLegalModal()
                    }}
                  >
                    이용약관 동의
                  </button>
                )}
                {legalModal.doc === 'privacy' && (
                  <button
                    type="button"
                    className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
                    onClick={() => {
                      setAgreements(prev => ({ ...prev, privacy: true }))
                      closeLegalModal()
                    }}
                  >
                    개인정보 처리방침 동의
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            회원가입 신청
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            소속 조직과 역할을 선택하고 정보를 입력해 주세요. 승인 후 사용 가능합니다.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                이메일 <span className="text-rose-600">*</span>
              </label>
              <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <input
                  type="text"
                  inputMode="email"
                  autoComplete="email"
                  required
                  aria-label="이메일 아이디"
                  className="w-full min-w-0 flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                  placeholder="이메일 아이디"
                  value={emailLocal}
                  onChange={(e) => setEmailLocal(e.target.value)}
                />
                <span className="hidden text-slate-500 sm:inline" aria-hidden>@</span>
                <div className="flex w-full min-w-0 flex-1 gap-2 sm:w-auto">
                  <select
                    aria-label="이메일 도메인"
                    className="w-full min-w-0 flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy sm:max-w-[11rem]"
                    value={emailDomainSel}
                    onChange={(e) => setEmailDomainSel(e.target.value)}
                  >
                    {EMAIL_DOMAIN_PRESETS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                    <option value="__custom__">직접 입력</option>
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => checkEmailNow(fullEmail)}
                  disabled={loading || emailCheck.status === 'checking' || !isValidEmail(fullEmail)}
                  className="shrink-0 rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                >
                  {emailCheck.status === 'checking' ? '확인 중...' : '중복 확인'}
                </button>
              </div>
              {emailDomainSel === '__custom__' && (
                <input
                  type="text"
                  className="mt-2 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                  placeholder="도메인 (예: company.com)"
                  value={emailDomainCustom}
                  onChange={(e) => setEmailDomainCustom(e.target.value.replace(/^@+/, ''))}
                  autoCapitalize="none"
                />
              )}
              {fullEmail && (
                <p className="mt-1 text-xs text-slate-500">
                  입력된 주소: <span className="font-mono text-slate-700">{fullEmail}</span>
                </p>
              )}
              {emailCheck.message && (
                <div
                  className={[
                    'mt-1 text-xs',
                    emailCheck.status === 'available' ? 'text-emerald-700' : '',
                    (emailCheck.status === 'unavailable' || emailCheck.status === 'invalid' || emailCheck.status === 'error') ? 'text-red-700' : '',
                    (emailCheck.status !== 'available' && emailCheck.status !== 'unavailable' && emailCheck.status !== 'invalid' && emailCheck.status !== 'error') ? 'text-slate-600' : '',
                  ].filter(Boolean).join(' ')}
                >
                  {emailCheck.message}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                이름 <span className="text-rose-600">*</span>
              </label>
              <input
                name="name"
                type="text"
                required
                className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                placeholder="홍길동"
                value={form.name}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                휴대폰 번호 <span className="text-rose-600">*</span>
              </label>
              <div className="mt-1 flex gap-2">
                <input
                  name="phoneNumber"
                  type="tel"
                  required
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                  placeholder="010-0000-0000"
                  value={form.phoneNumber}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => checkPhoneNow(form.phoneNumber)}
                  disabled={loading || phoneCheck.status === 'checking' || !isValidPhone(form.phoneNumber)}
                  className="shrink-0 rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                >
                  {phoneCheck.status === 'checking' ? '확인 중...' : '중복 확인'}
                </button>
              </div>
              {phoneCheck.message && (
                <div
                  className={[
                    'mt-1 text-xs',
                    phoneCheck.status === 'available' ? 'text-emerald-700' : '',
                    (phoneCheck.status === 'unavailable' || phoneCheck.status === 'invalid' || phoneCheck.status === 'error') ? 'text-red-700' : '',
                    (phoneCheck.status !== 'available' && phoneCheck.status !== 'unavailable' && phoneCheck.status !== 'invalid' && phoneCheck.status !== 'error') ? 'text-slate-600' : '',
                  ].filter(Boolean).join(' ')}
                >
                  {phoneCheck.message}
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between gap-2">
                <label className="block text-sm font-medium text-gray-700">
                  추천인 코드 (선택)
                </label>
                {prefilledReferralCode && (
                  <span className="text-xs text-emerald-700">공유 링크로 자동 입력됨</span>
                )}
              </div>
              <input
                name="referrerCode"
                type="text"
                className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                placeholder="예: RF12ABCD"
                value={form.referrerCode}
                onChange={handleChange}
              />
              <p className="mt-1 text-xs text-slate-500">
                추천 링크로 방문한 경우 자동 입력되며, 필요하면 직접 수정할 수 있습니다.
              </p>
            </div>

            <div className="rounded border border-slate-200 p-3 bg-slate-50/50">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={personalJoin}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setPersonalJoinConfirmOpen(true)
                    } else {
                      disablePersonalJoin()
                    }
                  }}
                  className="rounded border-slate-300 text-brand-navy focus:ring-brand-navy"
                />
                <span className="text-sm font-medium text-gray-700">개인회원으로 가입시 체크해 주세요</span>
              </label>
              <div className="mt-3 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs leading-5 text-slate-700">
                <p><span className="font-medium text-slate-900">개인회원</span>: 조직을 선택하지 않고 개인 단위로 가입합니다.</p>
                <p><span className="font-medium text-slate-900">조직회원</span>: 회사, 지점 등 특정 조직 소속으로 가입합니다.</p>
                <p className="text-slate-600">회사 또는 특정 조직 소속으로 이용하실 경우에는 조직회원으로 가입해 주세요.</p>
              </div>
              {personalJoin && (
                <div className="mt-3 space-y-3 pl-6 border-l-2 border-slate-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">조직명</label>
                    <input
                      type="text"
                      className="mt-1 w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                      placeholder="예: 메가 GA 강남지점"
                      value={affiliationText}
                      onChange={(e) => setAffiliationText(e.target.value)}
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      입력한 이름으로 1인 조직이 생성되며, 비우면 &quot;이름(개인)&quot; 형태로 만들어집니다.
                    </p>
                    <p className="mt-1 text-xs text-amber-900/90 leading-snug">
                      이 표기명은 설계의뢰 알림·진료분석 리포트 등에 그대로 사용되니, 실제 소속(회사·사업단·지점 등)과 동일하게 정확히 적어 주세요.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {!personalJoin && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                소속 조직 <span className="text-rose-600">*</span>
              </label>
                <div className="grid grid-cols-1 gap-2">
                  {/* 1단계: 카테고리 선택 (생보/손보/GA) */}
                  <select
                    className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                    value={orgCategory}
                    onChange={handleCategoryChange}
                  >
                    <option value="">구분 선택: 생보 / 손보 / GA</option>
                    {lifeRootIds.length > 0 && <option value="INS_LIFE">생보(생명보험)</option>}
                    {nonLifeRootIds.length > 0 && <option value="INS_NONLIFE">손보(손해보험)</option>}
                    {gaRootIds.length > 0 && <option value="GA">GA</option>}
                  </select>

                  {/* 2단계: 상위 조직 (보험사 / GA 본사 등) */}
                  <select
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                    value={orgL1}
                    onChange={handleLevel1Change}
                    disabled={!orgCategory || level1Options.length === 0}
                  >
                    <option value="">{orgCategory ? '상위 조직 선택' : '먼저 구분을 선택하세요'}</option>
                    {level1Options.map(o => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>

                  {/* 3단계: 1차 하위 */}
                  <select
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                    value={orgL2}
                    onChange={handleLevel2Change}
                    disabled={level2Options.length === 0}
                  >
                    <option value="">1차 하위 조직</option>
                    {level2Options.map(o => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>

                  {/* 4단계: 2차 하위 */}
                  <select
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                    value={orgL3}
                    onChange={handleLevel3Change}
                    disabled={level3Options.length === 0}
                  >
                    <option value="">2차 하위 조직 (선택 사항)</option>
                    {level3Options.map(o => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </div>
            </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700">
                신청 역할 <span className="text-rose-600">*</span>
              </label>
              {personalJoin && (
                <div className="mb-2 text-sm text-slate-600 bg-blue-50 border border-blue-200 rounded p-2">
                  개인 가입은 GA 관리자 역할로 자동 설정됩니다.
                </div>
              )}
              <select
                name="requestedRole"
                className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy disabled:bg-slate-100 disabled:cursor-not-allowed"
                value={form.requestedRole}
                onChange={handleChange}
                disabled={(!orgCategory && !personalJoin) || personalJoin}
              >
                {!orgCategory && !personalJoin ? (
                  <option value="">구분/조직을 선택하거나 개인회원 체크해 주세요</option>
                ) : personalJoin ? (
                  <option value={Roles.GA_MANAGER}>{roleLabel(Roles.GA_MANAGER)}</option>
                ) : (
                  filteredRoleOptions.map(code => (
                    <option key={code} value={code}>
                      {roleLabel(code)}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                비밀번호 <span className="text-rose-600">*</span>
              </label>
              <input
                name="password"
                type="password"
                required
                className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                placeholder="6자 이상"
                value={form.password}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                비밀번호 확인 <span className="text-rose-600">*</span>
              </label>
              <input
                name="confirmPassword"
                type="password"
                required
                className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                placeholder="비밀번호를 다시 입력하세요"
                value={form.confirmPassword}
                onChange={handleChange}
              />
            </div>

            <div className="rounded-md border bg-white p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-slate-800">약관 동의</div>
                <button
                  type="button"
                  className="text-xs text-slate-600 hover:text-brand-sky"
                  onClick={() => setAgreements({ terms: true, privacy: true, marketing: true })}
                >
                  전체 동의
                </button>
              </div>

              <div className="mt-3 space-y-2 text-sm">
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={agreements.terms}
                    onChange={(e) => setAgreements(prev => ({ ...prev, terms: e.target.checked }))}
                  />
                  <span className="flex-1">
                    <span className="text-rose-600">[필수]</span> 이용약관 동의
                    <button
                      type="button"
                      className="ml-2 text-xs text-slate-600 hover:text-brand-sky underline"
                      onClick={() => setLegalModal({ open: true, doc: 'terms' })}
                    >
                      보기
                    </button>
                  </span>
                </label>

                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={agreements.privacy}
                    onChange={(e) => setAgreements(prev => ({ ...prev, privacy: e.target.checked }))}
                  />
                  <span className="flex-1">
                    <span className="text-rose-600">[필수]</span> 개인정보 처리방침 동의
                    <button
                      type="button"
                      className="ml-2 text-xs text-slate-600 hover:text-brand-sky underline"
                      onClick={() => setLegalModal({ open: true, doc: 'privacy' })}
                    >
                      보기
                    </button>
                  </span>
                </label>

                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={agreements.marketing}
                    onChange={(e) => setAgreements(prev => ({ ...prev, marketing: e.target.checked }))}
                  />
                  <span className="flex-1">
                    [선택] 마케팅/광고성 정보 수신 동의
                  </span>
                </label>

                <div className="text-xs text-slate-500">
                  {requiredAgreed ? '필수 약관에 동의했습니다.' : '필수 약관(이용약관/개인정보 처리방침)에 동의해 주세요.'}
                  {legal.termsVersion || legal.privacyVersion ? (
                    <span className="ml-1">
                      (버전: {legal.termsVersion || '-'} / {legal.privacyVersion || '-'})
                    </span>
                  ) : null}
                </div>
                <div className="text-[11px] text-slate-400">선택 동의는 서비스 이용에 필수가 아닙니다.</div>
              </div>
            </div>
          </div>

          {error && <InlineError message={error} />}
          {success && <div className="text-sm text-green-700">{success}</div>}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-brand-navy hover:bg-brand-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-navy disabled:opacity-50"
            >
              {loading ? <Loading label="신청 중..." /> : '가입 신청'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

